import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_LAYERS_ADD,
  MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME,
  MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE,
  TEMPLATES_DUPLICATE,
  TEMPLATES_DELETE,
  TEMPLATES_LOCK_LAYER,
  TEMPLATES_UNLOCK_LAYER,
  TEMPLATES_HIDE_LAYER,
  TEMPLATES_SHOW_LAYER,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_TEXT,
} from "../../../i18n/keysTranslations";
import { findNodePath } from "../utils/layerTree";
import { flattenInstanceItems } from "../instance/instanceGraph";
import {
  layerListNoNativeSelectSx,
  preventLayerListNativeSelectStart,
  preventNativeTextSelectOnAdditivePointer,
} from "./canvasLayerSelection";
import SortableNestedLayerList from "./SortableNestedLayerList";

const LayerTreePanel = ({
  nodes = {},
  nodeLayouts = {},
  assets = {},
  tryingNodeIDs = {},
  selectedID,
  selectedIDs = [],
  expandedIDs,
  disabled,
  onSelect,
  onToggleExpand,
  onRename,
  onReorder,
  onDelete,
  onCopy,
  onToggleLock,
  onToggleVisible,
  onAddLayer,
}) => {
  const { t } = useTranslationApp();
  const [addMenuEl, setAddMenuEl] = useState(null);
  const items = useMemo(
    () => flattenInstanceItems({ nodes, nodeLayouts, assets }),
    [assets, nodeLayouts, nodes]
  );
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  const selectedParentID =
    findNodePath({ nodes, nodeID: selectedID })?.parentID || null;

  const onCloseAddMenu = () => setAddMenuEl(null);

  const onChooseLayerType = (type) => {
    onCloseAddMenu();
    onAddLayer({ type });
  };

  return (
    <Box
      onMouseDown={preventNativeTextSelectOnAdditivePointer}
      onSelectStart={preventLayerListNativeSelectStart}
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E4E4E7",
        borderRadius: "14px",
        p: 2,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        ...layerListNoNativeSelectSx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 0.5,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {t(MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            component="button"
            type="button"
            disabled={disabled}
            onClick={(event) => {
              if (disabled) return;
              setAddMenuEl(event.currentTarget);
            }}
            sx={{
              color: "#111",
              fontSize: 13,
              fontWeight: 700,
              cursor: disabled ? "default" : "pointer",
              whiteSpace: "nowrap",
              userSelect: "none",
              border: "none",
              bgcolor: "transparent",
              p: 0,
              fontFamily: "inherit",
              minWidth: 16,
              minHeight: 16,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-end",
              opacity: disabled ? 0.4 : 1,
              pointerEvents: disabled ? "none" : "auto",
            }}
          >
            {`+ ${t(MODAL_IMAGE_EDIT_LAYERS_ADD)}`}
          </Box>
        </Box>
      </Box>
      <Menu
        anchorEl={addMenuEl}
        open={Boolean(addMenuEl)}
        onClose={onCloseAddMenu}
      >
        <MenuItem onClick={() => onChooseLayerType("text")}>
          {t(TEMPLATES_PRODUCE_ADD_TEXT)}
        </MenuItem>
        <MenuItem onClick={() => onChooseLayerType("image")}>
          {t(TEMPLATES_PRODUCE_ADD_IMAGE)}
        </MenuItem>
      </Menu>
      <Box
        data-layer-list-scroll=""
        sx={{ flex: 1, overflow: "auto", overflowAnchor: "none", minHeight: 0, pr: 1 }}
      >
        {items.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <SortableNestedLayerList
              nodes={nodes}
              nodeLayouts={nodeLayouts}
              itemsById={itemsById}
              tryingNodeIDs={tryingNodeIDs}
              selectedID={selectedID}
              selectedIDs={selectedIDs}
              expandedIDs={
                selectedParentID
                  ? { ...expandedIDs, [selectedParentID]: true }
                  : expandedIDs
              }
              disabled={disabled}
              onSelect={(id, event) => onSelect(id, event)}
              onRename={onRename}
              onToggleExpand={onToggleExpand}
              onReorder={onReorder}
              onDelete={onDelete}
              onCopy={onCopy}
              onToggleLock={onToggleLock}
              onToggleVisible={onToggleVisible}
              fallbackName={t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
                index: 1,
              })}
              deleteLabel={t(TEMPLATES_DELETE)}
              copyLabel={t(TEMPLATES_DUPLICATE)}
              lockLabel={t(TEMPLATES_LOCK_LAYER)}
              unlockLabel={t(TEMPLATES_UNLOCK_LAYER)}
              hideLabel={t(TEMPLATES_HIDE_LAYER)}
              showLabel={t(TEMPLATES_SHOW_LAYER)}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default LayerTreePanel;
